import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import type {
  DiscoveryPartner,
  DiscoveryVenue,
  ManagedVenue,
  PartnerMetricsSnapshot,
  PublicPartnerDetail,
  PublicPartnerEventKey,
  PublicVenueDetail,
  VenueBootstrapResponse,
  VenueDetail,
  VenueRecord,
} from "@/types/services/api-contracts";
import {
  callEdgeData,
  callEdgeFunction,
  callPublicEdgeData,
  callPublicEdgeFunction,
} from "./_core/edge-call";

export const venueService = {
  async bootstrap(data: {
    tenant_name: string;
    venue_name: string;
    tax_id?: string | null;
    description?: string;
    address?: string;
    postal_code?: string | null;
    city: string;
    state: string;
    latitude?: number | null;
    longitude?: number | null;
    phone?: string;
    email?: string;
  }): Promise<VenueBootstrapResponse> {
    return callEdgeData<VenueBootstrapResponse>("manage-venue", "bootstrap", { body: data });
  },

  async create(data: {
    tenant_id: string;
    name: string;
    slug: string;
    tax_id?: string | null;
    description?: string;
    address?: string;
    postal_code?: string | null;
    city?: string;
    state?: string;
    phone?: string;
    email?: string;
    amenities?: string[];
    operating_hours?: Record<string, unknown>;
    cancellation_rules?: string;
    latitude?: number | null;
    longitude?: number | null;
    cover_image_url?: string | null;
    photos?: string[];
  }): Promise<Record<string, unknown>> {
    return callEdgeFunction("manage-venue", "create", { body: data });
  },

  async update(data: {
    venue_id: string;
    name?: string;
    tax_id?: string | null;
    description?: string;
    address?: string;
    postal_code?: string | null;
    city?: string;
    state?: string;
    phone?: string;
    email?: string;
    amenities?: string[];
    operating_hours?: Record<string, unknown>;
    cancellation_rules?: string;
    latitude?: number | null;
    longitude?: number | null;
    cover_image_url?: string | null;
    photos?: string[];
    partner_category?: string | null;
    partner_headline?: string | null;
    partner_offer_title?: string | null;
    partner_offer_description?: string | null;
    partner_offer_terms?: string | null;
    partner_cta_label?: string | null;
    partner_cta_url?: string | null;
    partner_target_sports?: string[] | null;
    partner_target_play_times?: string[] | null;
    status?: "active" | "inactive";
  }): Promise<Record<string, unknown>> {
    return callEdgeFunction("manage-venue", "update", { method: "PUT", body: data });
  },

  async softDelete(venueId: string): Promise<Record<string, unknown>> {
    return callEdgeFunction("manage-venue", "delete", {
      method: "DELETE",
      params: { venue_id: venueId },
    });
  },

  /** Read venues via direct Supabase query (RLS-protected) */
  async list(filters?: { city?: string; status?: string }): Promise<VenueRecord[]> {
    let query = supabase
      .from("venues")
      .select("*")
      .is("deleted_at", null)
      .order("created_at", { ascending: false });

    if (filters?.city) query = query.eq("city", filters.city);
    if (filters?.status) query = query.eq("status", filters.status as "active" | "inactive" | "pending_review" | "suspended");

    const { data, error } = await query;
    if (error) throw error;
    return data;
  },

  async discover(): Promise<DiscoveryVenue[]> {
    return callEdgeData<DiscoveryVenue[]>("manage-venue", "discover", {
      method: "GET",
    });
  },

  async discoverPartners(context?: {
    latitude?: number | null;
    longitude?: number | null;
    city?: string | null;
    neighborhood?: string | null;
    preferred_sports?: string[] | null;
    preferred_play_times?: string[] | null;
  }): Promise<DiscoveryPartner[]> {
    const params: Record<string, string> = {};

    if (typeof context?.latitude === "number" && Number.isFinite(context.latitude)) {
      params.latitude = String(context.latitude);
    }
    if (typeof context?.longitude === "number" && Number.isFinite(context.longitude)) {
      params.longitude = String(context.longitude);
    }
    if (context?.city?.trim()) {
      params.city = context.city.trim();
    }
    if (context?.neighborhood?.trim()) {
      params.neighborhood = context.neighborhood.trim();
    }
    if (context?.preferred_sports?.length) {
      params.preferred_sports = context.preferred_sports.map((item) => item.trim()).filter(Boolean).join(",");
    }
    if (context?.preferred_play_times?.length) {
      params.preferred_play_times = context.preferred_play_times.map((item) => item.trim()).filter(Boolean).join(",");
    }

    return callEdgeData<DiscoveryPartner[]>("manage-venue", "discover-partners", {
      method: "GET",
      params,
    });
  },

  async trackPartnerEvent(data: {
    partner_slug: string;
    event_key: PublicPartnerEventKey;
    source?: string | null;
  }): Promise<Record<string, unknown>> {
    return callPublicEdgeFunction("manage-venue", "track-partner-event", {
      method: "POST",
      body: data,
    });
  },

  async getPublicVenueBySlug(venueSlug: string): Promise<PublicVenueDetail> {
    return callPublicEdgeData<PublicVenueDetail>("manage-venue", "public-venue", {
      method: "GET",
      params: {
        venue_slug: venueSlug,
      },
    });
  },

  async getPublicPartnerBySlug(venueSlug: string): Promise<PublicPartnerDetail> {
    return callPublicEdgeData<PublicPartnerDetail>("manage-venue", "public-partner", {
      method: "GET",
      params: {
        venue_slug: venueSlug,
      },
    });
  },

  async getPartnerMetrics(venueId: string): Promise<PartnerMetricsSnapshot> {
    return callEdgeData<PartnerMetricsSnapshot>("manage-venue", "partner-metrics", {
      method: "GET",
      params: { venue_id: venueId },
    });
  },

  async listManaged(tenantIds: string[]): Promise<ManagedVenue[]> {
    if (!tenantIds.length) return [] as ManagedVenue[];

    const { data, error } = await supabase
      .from("venues")
      .select(`
        id,
        tenant_id,
        name,
        slug,
        status,
        tax_id,
        address,
        postal_code,
        city,
        state,
        latitude,
        longitude,
        rating,
        total_reviews,
        amenities,
        partner_category,
        partner_headline,
        partner_offer_title,
        partner_offer_description,
        partner_offer_terms,
        partner_cta_label,
        partner_cta_url,
        partner_target_sports,
        partner_target_play_times,
        operating_hours,
        cancellation_rules,
        cover_image_url,
        photos,
        stripe_connect_account_id,
        stripe_connect_charges_enabled,
        stripe_connect_payouts_enabled,
        stripe_connect_details_submitted,
        stripe_connect_disabled_reason,
        stripe_connect_updated_at,
        courts (
          id,
          name,
          sport_id,
          surface_type,
          indoor,
          has_lighting,
          capacity,
          active,
          pricing_rules (
            id,
            day_of_week,
            start_time,
            end_time,
            price_cents,
            is_peak,
            active,
            label,
            priority
          ),
          sports (
            name,
            icon
          )
        )
      `)
      .in("tenant_id", tenantIds)
      .is("deleted_at", null)
      .order("name");

    if (error) throw error;

    return (data ?? []) as unknown as ManagedVenue[];
  },

  async getById(venueId: string): Promise<VenueDetail> {
    const { data, error } = await supabase
      .from("venues")
      .select("*, courts(*), venue_sports(*, sports(*))")
      .eq("id", venueId)
      .is("deleted_at", null)
      .single();
    if (error) throw error;
    return data as VenueDetail;
  },
};

